// Client-side error reporter: batches browser errors to sinek-api's
// POST /monitor/report. Hard rules: never report the reporter's own failures
// (recursion guard), one full payload per fingerprint per page load (then
// counts only), max 50 distinct fingerprints per page load, 10s batch flush,
// sendBeacon on pagehide. Anonymous sessions get a 401 — swallowed silently.

type ReportContext = Record<string, string | number | boolean | null>;

interface PendingClientError {
  name: string;
  message: string;
  stack?: string;
  url: string;
  count: number;
  context?: ReportContext;
}

const FLUSH_INTERVAL_MS = 10_000;
const MAX_FINGERPRINTS_PER_PAGELOAD = 50;
const MAX_BATCH = 20;
const MAX_STACK_LENGTH = 8192;

const pending = new Map<string, PendingClientError>();
const seen = new Set<string>();
let initialized = false;
let isReporting = false;

// Browser-emitted noise that is not an application bug and must never reach the
// monitor, otherwise it buries real signal. Add patterns here deliberately —
// each entry is a class of error that is provably benign and unactionable.
//   - "ResizeObserver loop ..." : the browser fires this when an observer
//     callback dirties layout and needs another pass. Nothing breaks; every
//     major site ignores it. It floods the monitor from virtualized lists.
const IGNORED_MESSAGE_PATTERNS: RegExp[] = [/ResizeObserver loop/i];

const isIgnorableError = (message: string): boolean =>
  IGNORED_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));

// Cheap local batching key (the server re-fingerprints authoritatively).
const localKey = (name: string, message: string): string =>
  `${name}|${message.replace(/\d+/g, "#").slice(0, 120)}`;

const apiBase = process.env.NEXT_PUBLIC_API_URL ?? "";

const flush = (useBeacon = false): void => {
  if (pending.size === 0) return;
  // Send at most MAX_BATCH groups per flush; the rest stay queued for the
  // next cycle instead of being dropped.
  const batch = [...pending.entries()].slice(0, MAX_BATCH);
  for (const [key] of batch) pending.delete(key);
  const errors = batch.map(([, value]) => value);
  const payload = JSON.stringify({ errors });

  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      // text/plain avoids a CORS preflight that can drop the beacon; the
      // server's report route parses string bodies explicitly.
      navigator.sendBeacon(
        `${apiBase}/monitor/report`,
        new Blob([payload], { type: "text/plain" })
      );
      return;
    }
    void fetch(`${apiBase}/monitor/report`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true
    }).catch(() => undefined);
  } catch {
    // Reporting must never throw.
  }
};

export const reportError = (input: {
  name: string;
  message: string;
  stack?: string;
  context?: ReportContext;
}): void => {
  if (isReporting) return;
  if (isIgnorableError(input.message)) return;
  isReporting = true;
  try {
    const key = localKey(input.name, input.message);
    const existing = pending.get(key);
    if (existing) {
      existing.count += 1;
    } else if (seen.has(key)) {
      // Already fully reported this page load — count-only entry.
      pending.set(key, {
        name: input.name,
        message: input.message,
        url: window.location.pathname,
        count: 1,
        context: input.context
      });
    } else if (seen.size < MAX_FINGERPRINTS_PER_PAGELOAD) {
      seen.add(key);
      pending.set(key, {
        name: input.name,
        message: input.message,
        stack: input.stack?.slice(0, MAX_STACK_LENGTH),
        url: window.location.pathname,
        count: 1,
        context: input.context
      });
    }
    // Beyond the cap: drop silently.
  } catch {
    // Never throw from the reporter.
  } finally {
    isReporting = false;
  }
};

export const initErrorReporter = (): void => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("error", (event) => {
    // Cross-origin scripts give an opaque "Script error." — bucketed as one.
    const error = event.error as Error | undefined;
    reportError({
      name: error?.name ?? "WindowError",
      message: event.message || "Script error.",
      stack: error?.stack
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    reportError(
      reason instanceof Error
        ? { name: reason.name, message: reason.message, stack: reason.stack }
        : { name: "UnhandledRejection", message: String(reason) }
    );
  });

  setInterval(() => flush(), FLUSH_INTERVAL_MS);
  window.addEventListener("pagehide", () => flush(true));
};
